import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import { Shield, AlertTriangle, Eye, EyeOff, Camera, Clock, Activity, Video } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { API } from '@/config/api';
import usePhase1Monitor from '@/hooks/usePhase1Monitor';
import { faceDetectionService } from '@/utils/faceDetection';

function DocumentLockScreen({ lockReason, udtId, token, API, onExit }) {
  const [requestSent, setRequestSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [documentLocked, setDocumentLocked] = useState(true);

  useEffect(() => {
    if (!documentLocked) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/access/token-info`, {
          params: { udt_id: udtId, token },
        });

        if (res.data.status === 'active') {
          console.log('[Viewer] Access restored - reloading');
          clearInterval(interval);
          window.location.reload();
        }
      } catch (e) {
        // Silent - keep polling
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [documentLocked, udtId, token, API]);

  const handleRequestAccess = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/monitor/request-reaccess`, {
        udt_id: udtId,
        token,
        reason: lockReason,
      });
      setRequestSent(true);
      console.log('[Viewer] Re-access request sent to owner');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setRequestSent(false);
      
      if (status === 429) {
        if (detail === 'MAX_REQUESTS_EXCEEDED') {
          alert('You have used all 3 access requests for this document. Contact the document owner for a new link.');
        } else {
          alert('A request is already pending. Wait for owner response.');
        }
      } else {
        alert('Failed to send request. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        background: 'linear-gradient(135deg, #0f172a, #1e1b4b)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div
        style={{
          background: '#1f2937',
          border: '1px solid #ef4444',
          borderRadius: 20,
          padding: '48px 40px',
          maxWidth: 440,
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(239,68,68,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: 40,
          }}
        >
          🔒
        </div>

        <h2
          style={{
            color: 'white',
            fontSize: 22,
            marginBottom: 12,
            fontWeight: 700,
          }}
        >
          Document Access Revoked
        </h2>

        <div
          style={{
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
            padding: '10px 16px',
            marginBottom: 20,
          }}
        >
          <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>
            {lockReason || 'Security violation detected'}
          </p>
        </div>

        <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 32 }}>
          Your access was revoked by the security monitoring system.
          Request the document owner to restore your access.
        </p>

        {requestSent ? (
          <div
            style={{
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid #22c55e',
              borderRadius: 10,
              padding: 16,
              marginBottom: 12,
              color: '#22c55e',
              fontSize: 14,
            }}
          >
            ✓ Request sent — owner will be notified shortly
          </div>
        ) : (
          <button
            onClick={handleRequestAccess}
            disabled={loading}
            style={{
              width: '100%',
              padding: 14,
              background: loading ? '#374151' : '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: 12,
            }}
          >
            {loading ? 'Sending...' : '📩 Request Access Again'}
          </button>
        )}

        <button
          onClick={() => navigate('/dashboard')}
          style={{
            width: '100%',
            padding: 14,
            background: 'transparent',
            border: '1px solid #374151',
            color: '#9ca3af',
            borderRadius: 10,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          💬 Exit to Chat
        </button>

        <p style={{ color: '#374151', fontSize: 11, marginTop: 24 }}>
          If you believe this is an error, contact support.
        </p>
      </div>
    </div>
  );
}

export default function EnhancedViewer() {
  const { udtId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const verified = searchParams.get('verified');

  const [pdfUrl, setPdfUrl] = useState(null);
  const [nonPdfUrl, setNonPdfUrl] = useState(null);
  const [docMime, setDocMime] = useState('application/pdf');
  const [docInfo, setDocInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docReady, setDocReady] = useState(false);
  const [monitorReady, setMonitorReady] = useState(false);
  const [aiMonitoring, setAiMonitoring] = useState(false);
  const [violations, setViolations] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [mouseActivity, setMouseActivity] = useState(0);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [micActive, setMicActive] = useState(false);
  const [warningMessage, setWarningMessage] = useState(null);
  const [documentLocked, setDocumentLocked] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [documentContent, setDocumentContent] = useState(null); // Optional: kept for backward compatibility (meta endpoint no longer sets this)
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [monitorSessionId, setMonitorSessionId] = useState(null);
  const [permissions, setPermissions] = useState('view_only');
  const [numPages, setNumPages] = useState(null);
  const [faceShieldActive, setFaceShieldActive] = useState(false);
  
  const webcamRef = useRef(null);
  const documentFrameRef = useRef(null);
  const monitoringInterval = useRef(null);
  const sessionInterval = useRef(null);
  const lastMousePos = useRef({ x: 0, y: 0 });
  const audioContext = useRef(null);
  const audioAnalyser = useRef(null);
  const audioStream = useRef(null);

  // DEBUG: Log webcam ref and session state
  useEffect(() => {
    console.log('[Phase1-DEBUG] Webcam ref available:', !!webcamRef?.current?.video);
  }, []);

  useEffect(() => {
    console.log('[Phase1-DEBUG] sessionId:', monitorSessionId, 'token:', !!token, 'udtId:', udtId);
  }, [monitorSessionId, token, udtId]);

  // Global protection: disable right-click, clicks on PDF area, and risky keyboard shortcuts (PrintScreen / Ctrl+P / Ctrl+S / Ctrl+C)
  useEffect(() => {
    // Disable right-click context menu everywhere (capture phase so it also blocks inside PDF viewer where possible)
    const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Disable mouse clicks on the PDF view space, but keep scroll (wheel) working.
    const handleMouseClickCapture = (e) => {
      const target = e.target;
      // If the event originated from the PDF iframe or its immediate wrapper, block it.
      if (target?.tagName === 'IFRAME' || target?.closest?.('[data-pdf-viewer-wrapper=\"true\"]')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      return undefined;
    };

    // Disable keyboard shortcuts for copy, print, save
    const handleKeyDown = (e) => {
      const key = (e.key || '').toLowerCase();
      if (
        (e.ctrlKey || e.metaKey) &&
        ['c', 'p', 's'].includes(key)
      ) {
        e.preventDefault();
        return false;
      }
      // Disable F12 devtools and print screen
      if (e.key === 'F12' || e.key === 'PrintScreen') {
        e.preventDefault();
        return false;
      }
    };

    // Disable drag (prevents drag-to-save images) but allow drag inside PDF iframe (e.g. scrollbar)
    const handleDragStart = (e) => {
      const target = e.target;
      if (target?.tagName === 'IFRAME' || target?.closest?.('iframe')) return;
      e.preventDefault();
      return false;
    };

    // Capture at window + document level so context menu and clicks never reach browser default,
    // including for embedded viewers when the browser allows it.
    window.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('contextmenu', handleContextMenu, true);
    window.addEventListener('mousedown', handleMouseClickCapture, true);
    window.addEventListener('mouseup', handleMouseClickCapture, true);
    window.addEventListener('click', handleMouseClickCapture, true);
    window.addEventListener('dblclick', handleMouseClickCapture, true);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('dragstart', handleDragStart);

    return () => {
      window.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('contextmenu', handleContextMenu, true);
      window.removeEventListener('mousedown', handleMouseClickCapture, true);
      window.removeEventListener('mouseup', handleMouseClickCapture, true);
      window.removeEventListener('click', handleMouseClickCapture, true);
      window.removeEventListener('dblclick', handleMouseClickCapture, true);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, []);

  useEffect(() => {
    // Check if verified OR if they have a valid share token
    // Direct shares from messages include a token and should bypass OTP
    const isVerified = verified === 'true';
    const hasToken = !!token;
    const allowAccess = isVerified || hasToken;

    if (!allowAccess) {
      // No verification and no token - require OTP
      navigate(`/viewer-auth/${udtId}?token=${token}`);
      return;
    }

    if (!hasToken) {
      // Verified but missing token
      toast.error('Access token required');
      return;
    }
    
    // CRITICAL: Stop rrweb recording when viewer page mounts to prevent memory leak
    // rrweb captures canvas frames continuously which causes OOM crash
    if (window.__rrweb_stop__) {
      window.__rrweb_stop__();
      window.__rrweb_stop__ = null;
      console.log('[EnhancedViewer] rrweb recording stopped');
    }
    if (window.rrwebRecorder?.stop) {
      window.rrwebRecorder.stop();
      console.log('[EnhancedViewer] rrweb recorder stopped');
    }
    
    // loadDocument() removed - now handled by fetchDocument useEffect
    setupSecurityMeasures();
    startSessionTracking();
    initializeMonitoringSession();
    
    return () => {
      cleanup();
      faceDetectionService.destroy();
    };
  }, []);

  /**
   * Initialize monitoring session (creates session ID for face monitoring)
   */
  const initializeMonitoringSession = async () => {
    try {
      // Generate session ID for this monitoring instance
      const sessionId = `session_${Date.now()}_${Math.random().toString(16).slice(2)}`;
      setMonitorSessionId(sessionId);
      console.log('[Phase1] Monitoring session initialized:', sessionId);
      setMonitorReady(true);
    } catch (error) {
      console.error('[Phase1] Failed to initialize session:', error);
      toast.warning('Monitoring session setup failed');
    }
  };

  const lockDocument = useCallback((reason) => {
    console.log('[Viewer] lockDocument called:', reason);
    
    // 1. Stop monitoring immediately
    setAiMonitoring(false);
    
    // 2. Set locked state
    setDocumentLocked(true);
    setLockReason(reason);
    
    // 3. Clear document content from memory (user cannot see it)
    setDocumentContent(null);
    setPdfUrl(null);
    
    // 4. Stop webcam
    if (webcamRef?.current?.stream) {
      try {
        webcamRef.current.stream.getTracks().forEach(t => t.stop());
      } catch {}
    }
    
    console.log('[Viewer] Document locked successfully');
  }, []);

  const handleMonitorAction = useCallback((data) => {
    console.log('[Viewer] handleMonitorAction called with:', data);
    
    if (!data || !data.action) {
      console.warn('[Viewer] Invalid action data:', data);
      return;
    }

    if (data.action === 'warn') {
      setWarningMessage(data.reason || 'Security warning');
      toast.warning(data.reason || 'Security warning', { duration: 5000 });
      setTimeout(() => setWarningMessage(null), 5000);
      return;
    }
    
    if (data.action === 'lock') {
      console.log('[Viewer] 🔒 LOCKING NOW:', data.reason);
      lockDocument(data.reason || 'Security violation');
      return;
    }
    
    console.warn('[Viewer] Unknown action:', data.action);
  }, [lockDocument]);

  /**
   * Show non-blocking warning overlay
   */
  function showWarningOverlay(message) {
    setWarningMessage(message);
    toast.warning(message, { duration: 5000 });
    
    // Auto-hide after 5 seconds
    setTimeout(() => {
      setWarningMessage(null);
    }, 5000);
  }

  // Phase 1 Monitor Hook (must be declared AFTER handleMonitorAction to avoid TDZ)
  const { faceCount, status: monitorStatus } = usePhase1Monitor({
    enabled: true, // Monitoring is always enabled while viewer is open
    webcamRef,
    udtId,
    token,
    monitorSessionId,
    onAction: handleMonitorAction
  });

  // When monitoring is active and no face is detected, immediately shield the document view
  // and show a "no face / webcam issue" overlay. This reacts within a few hundred milliseconds.
  useEffect(() => {
    let timer;
    if (aiMonitoring && faceCount === 0) {
      // tiny debounce to avoid flicker during detector startup
      timer = setTimeout(() => setFaceShieldActive(true), 150);
    } else {
      setFaceShieldActive(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [aiMonitoring, faceCount]);

  // Prevent back navigation / tab close while document is open in monitoring mode
  useEffect(() => {
    if (!documentLoaded) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Document is still open in monitoring mode.';
      return e.returnValue;
    };

    const handlePopState = () => {
      // Push state back to prevent back navigation
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [documentLoaded]);

  const setupSecurityMeasures = () => {
    // Disable right-click
    document.addEventListener('contextmenu', (e) => e.preventDefault());
    
    // Disable keyboard shortcuts
    document.addEventListener('keydown', handleKeyDown);
    
    // Detect tab switch
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Track mouse activity
    document.addEventListener('mousemove', handleMouseMove);
    
    // Disable text selection
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    
    // Disable copy
    document.addEventListener('copy', (e) => {
      e.preventDefault();
      toast.error('Copy disabled for security');
    });
    
    // Add watermark overlay
    addWatermarkOverlay();
  };

  const handleKeyDown = (e) => {
    // Disable Print Screen, Ctrl+P, Ctrl+S, Ctrl+C
    if (
      e.key === 'PrintScreen' ||
      (e.ctrlKey && (e.key === 'p' || e.key === 's' || e.key === 'c')) ||
      (e.metaKey && (e.key === 'p' || e.key === 's' || e.key === 'c'))
    ) {
      e.preventDefault();
      addViolation('KEYBOARD_SHORTCUT', `Attempted: ${e.key}`);
      toast.warning('This action is disabled');
    }
  };

  const handleMouseMove = (e) => {
    const moved = Math.abs(e.clientX - lastMousePos.current.x) + Math.abs(e.clientY - lastMousePos.current.y);
    if (moved > 10) {
      setMouseActivity(prev => prev + 1);
      lastMousePos.current = { x: e.clientX, y: e.clientY };
    }
  };

  const addWatermarkOverlay = () => {
    const watermark = document.createElement('div');
    watermark.id = 'udt-watermark';
    watermark.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      background-image: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 200px,
        rgba(15, 82, 186, 0.03) 200px,
        rgba(15, 82, 186, 0.03) 400px
      );
    `;
    watermark.innerHTML = `
      <div style="
        position: absolute;
        top: 10px;
        right: 10px;
        padding: 8px 12px;
        background: rgba(15, 82, 186, 0.1);
        border: 1px solid rgba(15, 82, 186, 0.3);
        border-radius: 6px;
        font-size: 12px;
        color: #0F52BA;
        font-weight: 600;
      ">
        UDT ID: ${udtId} | Session: ${new Date().toLocaleTimeString()}
      </div>
    `;
    document.body.appendChild(watermark);
  };

  const cleanup = () => {
    document.removeEventListener('contextmenu', (e) => e.preventDefault());
    document.removeEventListener('keydown', handleKeyDown);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.removeEventListener('mousemove', handleMouseMove);
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';

    // Mark document as no longer loaded before stopping monitoring
    setDocumentLoaded(false);
    
    stopMonitoring();
    
    if (sessionInterval.current) {
      clearInterval(sessionInterval.current);
      sessionInterval.current = null;
    }
    
    const watermark = document.getElementById('udt-watermark');
    if (watermark) {
      watermark.remove();
    }
    
    // Release webcam ref
    if (webcamRef.current) {
      webcamRef.current = null;
    }
    
    // CRITICAL: Destroy MediaPipe to free 60MB WASM memory
    // This prevents memory from accumulating when user navigates between documents
    faceDetectionService.destroy();
    
    console.log('[Phase1] Cleanup complete');
  };

  const startSessionTracking = () => {
    sessionInterval.current = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      setTabSwitchCount(prev => prev + 1);
      addViolation('TAB_SWITCH', 'User switched to another tab');
      toast.warning('Tab switch detected! Document may be locked.');
      
      // Lock after 3 tab switches
      if (tabSwitchCount >= 2) {
        reportMisuse('EXCESSIVE_TAB_SWITCH', null);
      }
    }
  };

  // Fetch lightweight metadata (permissions, file_type) and prepare viewer URLs.
  const fetchDocument = async () => {
    try {
      console.log('[Viewer] GET meta', `${API}/viewer/meta/${udtId}?token=${token}`);
      const resp = await axios.get(`${API}/viewer/meta/${udtId}?token=${token}`);

      const meta = resp.data || {};
      const contentType = String(meta.file_type || 'application/pdf').toLowerCase();
      setDocMime(contentType);
      setPermissions(meta.permissions || 'view_only');

      // Build direct streaming URL for the iframe/image.
      const directUrl = `${API}/viewer/${udtId}?token=${encodeURIComponent(token)}`;
      setDocumentLoaded(true);
      setDocReady(true);

      if (contentType.includes('pdf')) {
        setPdfUrl(directUrl);
        setNonPdfUrl(null);
      } else if (contentType.startsWith('image/')) {
        setNonPdfUrl(directUrl);
        setPdfUrl(null);
      } else {
        setPdfUrl(null);
        setNonPdfUrl(null);
      }

      // Start AI monitoring automatically after document loads
      setTimeout(() => {
        startMonitoring();
      }, 2000);
    } catch (err) {
      console.error('[Viewer] Fetch failed:', err.response?.status, err.response?.data);
      setLoading(false);
      setDocReady(true);
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        if (detail === 'access_revoked') {
          lockDocument('Access previously revoked.');
        } else {
          toast.error('Access denied.');
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to load document');
      }
    }
  };

  // No blob URLs are created anymore, so no cleanup is required here.

  // BUG 1 FIX: useEffect that only depends on udtId and token
  useEffect(() => {
    if (!udtId || !token) {
      console.error('[Viewer] Missing udtId or token');
      return;
    }
    console.log('[Viewer] Fetching document...');
    fetchDocument();
  }, [udtId, token]);

  // Unified readiness: show viewer as soon as monitoring session is ready,
  // while the document can continue loading in the background.
  useEffect(() => {
    if (monitorReady) {
      setLoading(false);
    }
  }, [monitorReady]);

  const loadDocument = async () => {
    // Legacy function - keeping for backward compatibility
    // Now fetchDocument handles the actual fetching
    fetchDocument();
  };

  const startMonitoring = async () => {
    setAiMonitoring(true);
    toast.success('Face monitoring activated (Phase 1)');
    console.log('[Phase1] Monitoring started');
    
    // Audio monitoring is Phase 2 feature - disabled for Phase 1 to reduce memory usage
    // Previously caused continuous rAF loop consuming memory
  };
  
  const startMicrophoneMonitoring = async () => {
    // Disabled for Phase 1 - was consuming memory with continuous rAF loop
    // Will be re-enabled in Phase 2
    return;
  };
  
  const monitorAudioLevel = () => {
    // Disabled for Phase 1 - was consuming memory with continuous rAF loop
    // Will be re-enabled in Phase 2
    return;
  };

  const stopMonitoring = () => {
    // GUARD: Never allow manual stop while document is still open
    if (documentLoaded) {
      console.warn('[Phase1] Stop blocked - document still open');
      return;
    }

    setAiMonitoring(false);
    if (monitoringInterval.current) {
      clearInterval(monitoringInterval.current);
      monitoringInterval.current = null;
    }
    
    // Stop microphone (if it was started)
    if (audioStream.current) {
      audioStream.current.getTracks().forEach(track => {
        track.stop();
      });
      audioStream.current = null;
    }
    
    // Close audio context
    if (audioContext.current && audioContext.current.state !== 'closed') {
      audioContext.current.close();
      audioContext.current = null;
    }
    
    // Clean up audio analyser
    audioAnalyser.current = null;
    setMicActive(false);
    console.log('[Phase1] Monitoring stopped');
  };

  const addViolation = (type, message) => {
    const newViolation = { type, message, timestamp: new Date() };
    setViolations(prev => [...prev, newViolation]);
  };

  const reportMisuse = async (violationType, faceImage) => {
    try {
      await axios.post(`${API}/misuse/detect`, {
        udt_id: udtId,
        token: token,
        violation_type: violationType,
        face_image: faceImage,
        screen_hash: window.location.href
      });
      
      lockDocument(`Security violation detected: ${violationType}`);
    } catch (error) {
      console.error('[Phase1] Failed to report misuse:', error);
      toast.error('Failed to report security violation');
    }
  };

  const handleDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages || 1);
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/viewer/${udtId}?token=${token}`, {
        responseType: 'blob',
      });

      if (!response.data) {
        throw new Error('No document data');
      }

      let filename = `UDT_${udtId}`;
      const contentDisposition = response.headers['content-disposition'];
      const contentType = (response.headers['content-type'] || response.data.type || '').toLowerCase();

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const hasExt = /\.[^/.]+$/.test(filename);
      if (!hasExt) {
        if (contentType.includes('pdf')) filename += '.pdf';
        else if (contentType.includes('png')) filename += '.png';
        else if (contentType.includes('jpeg') || contentType.includes('jpg')) filename += '.jpg';
        else filename += '.bin';
      }

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    } catch (error) {
      console.error('[Viewer] Download failed:', error);
      toast.error('Failed to download document');
    }
  };

  const handleExit = () => {
    if (window.confirm('Exit the document viewer?')) {
      window.location.href = '/dashboard/messages';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E8F2FF 0%, #F5F9FF 100%)' }}>
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p style={{ color: '#0F52BA' }}>Loading secure document...</p>
        </div>
      </div>
    );
  }

  if (isLocked) {
    return (
      <DocumentLockScreen
        lockReason={lockReason}
        udtId={udtId}
        token={token}
        API={API}
        onExit={() => navigate('/dashboard')}
      />
    );
  }

  return (
    <>
      {documentLocked ? (
        <DocumentLockScreen
          lockReason={lockReason}
          udtId={udtId}
          token={token}
          API={API}
          onExit={() => navigate('/dashboard')}
        />
      ) : (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #E8F2FF 0%, #F5F9FF 100%)' }}>
      {/* Warning Overlay */}
      {warningMessage && (
        <div className="fixed inset-0 pointer-events-none z-[9998] flex items-start justify-center pt-20">
          <div className="pointer-events-auto bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 max-w-md shadow-lg animate-pulse">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-900">Security Warning</p>
                <p className="text-sm text-yellow-800 mt-1">{warningMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-10" style={{ borderColor: '#E8F2FF' }}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6" style={{ color: '#0F52BA' }} />
            <div>
              <h1 className="font-bold" style={{ color: '#0F52BA' }}>Secure Document Viewer (Phase 1)</h1>
              <p className="text-xs" style={{ color: '#666' }}>UDT ID: {udtId}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {Math.floor(sessionTime / 60)}:{(sessionTime % 60).toString().padStart(2, '0')}
            </Badge>
            <Badge variant="outline" className="flex items-center gap-1">
              <Activity className="w-3 h-3" />
              Activity: {mouseActivity}
            </Badge>
            {aiMonitoring && faceCount >= 0 && (
              <Badge className={faceCount <= 1 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                👤 Faces: {faceCount}
              </Badge>
            )}
            {aiMonitoring && (
              <Badge className="bg-green-100 text-green-800">
                <Eye className="w-3 h-3 mr-1" />
                Face Monitor
              </Badge>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Document Viewer */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Document View (No Download Mode)</CardTitle>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Badge variant="outline">🚫 Right-click disabled</Badge>
                  <Badge variant="outline">🚫 Screenshots blocked</Badge>
                  <Badge variant="outline">🚫 Copy disabled</Badge>
                  <Badge variant="outline">🚫 Print disabled</Badge>
                </div>
              </CardHeader>
              <CardContent>
                {/* Download + Exit controls above the PDF viewer */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginBottom: '12px',
                  }}
                >
                  {(permissions === 'download_view' || permissions === 'print_download_view') && (
                    <button
                      onClick={handleDownload}
                      style={{
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        fontWeight: 600,
                        fontSize: '15px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      ⬇ Download
                    </button>
                  )}

                  <button
                    onClick={handleExit}
                    style={{
                      background: '#1a1a1a',
                      color: 'white',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      fontWeight: 600,
                      fontSize: '15px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    → Exit
                  </button>
                </div>

                {/* Secure document view:
                    - PDF: use iframe with blob URL (existing behavior)
                    - Image: render inline image preview
                    - Other: show simple notice and rely on Download */}
                {pdfUrl && docMime.includes('pdf') && (
                  <div
                    className="relative"
                    style={{
                      width: '100%',
                      border: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      height: 'calc(100vh - 80px)',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                    data-pdf-viewer-wrapper="true"
                  >
                    {/* Watermark overlay to discourage screenshots */}
                    <div
                      className="watermark pointer-events-none select-none"
                      style={{
                        position: 'fixed',
                        inset: 0,
                        opacity: 0.1,
                        zIndex: 5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        fontSize: '48px',
                        fontWeight: 700,
                        color: '#0F52BA',
                        textTransform: 'uppercase',
                        mixBlendMode: 'multiply',
                      }}
                    >
                      Secure UDT Document
                    </div>
                    <iframe
                      src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                      className="w-full h-full"
                      tabIndex={0}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                      }}
                      title="Document"
                    />
                    {/* Shield the document instantly when no face is detected */}
                    {faceShieldActive && (
                      <div
                        className="absolute inset-0 z-30 flex flex-col items-center justify-center px-4 text-center"
                        style={{ background: 'rgba(15,23,42,0.94)' }}
                      >
                        <div
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: '9999px',
                            background: 'rgba(15,82,186,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 16,
                          }}
                        >
                          <Camera className="w-10 h-10" style={{ color: '#0F52BA' }} />
                        </div>
                        <h2 className="text-lg font-semibold mb-2" style={{ color: '#e5e7eb' }}>
                          Face not detected
                        </h2>
                        <p className="text-sm max-w-md" style={{ color: '#9ca3af' }}>
                          We cannot detect your face. Please ensure your webcam is on, uncovered, and your face is clearly visible in the frame.
                        </p>
                      </div>
                    )}
                    {/* Overlay only over the content area, leaving scrollbar gutter clickable/scrollable */}
                    <div
                      className="absolute z-20"
                      style={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 18, // leave ~18px on the right side for the native scrollbar
                        background: 'transparent',
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      }}
                    />
                  </div>
                )}

                {!pdfUrl && nonPdfUrl && docMime.startsWith('image/') && (
                  <div
                    className="relative"
                    style={{
                      width: '100%',
                      border: 'none',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      height: 'calc(100vh - 80px)',
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      className="watermark pointer-events-none select-none"
                      style={{
                        position: 'fixed',
                        inset: 0,
                        opacity: 0.1,
                        zIndex: 5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        fontSize: '48px',
                        fontWeight: 700,
                        color: '#0F52BA',
                        textTransform: 'uppercase',
                        mixBlendMode: 'multiply',
                      }}
                    >
                      Secure UDT Document
                    </div>
                    <img
                      src={nonPdfUrl}
                      alt="Secure document"
                      style={{
                        width: '90%',
                        height: '90%',
                        objectFit: 'contain',
                        display: 'block',
                        margin: '0 auto',
                        borderRadius: 8,
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                      }}
                    />
                    {faceShieldActive && (
                      <div
                        className="absolute inset-0 z-30 flex flex-col items-center justify-center px-4 text-center"
                        style={{ background: 'rgba(15,23,42,0.94)' }}
                      >
                        <div
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: '9999px',
                            background: 'rgba(15,82,186,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: 16,
                          }}
                        >
                          <Camera className="w-10 h-10" style={{ color: '#0F52BA' }} />
                        </div>
                        <h2 className="text-lg font-semibold mb-2" style={{ color: '#e5e7eb' }}>
                          Face not detected
                        </h2>
                        <p className="text-sm max-w-md" style={{ color: '#9ca3af' }}>
                          We cannot detect your face. Please ensure your webcam is on, uncovered, and your face is clearly visible in the frame.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {!pdfUrl && !nonPdfUrl && (
                  <div className="flex items-center justify-center h-64 text-sm text-gray-600">
                    {docReady
                      ? 'Preview not available for this file type. Use the Download button above to open it.'
                      : 'Loading document securely...'}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monitoring Panel */}
          <div className="space-y-4">
            {/* Webcam */}
            {aiMonitoring && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Phase 1: Face Monitoring</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative rounded-lg overflow-hidden" style={{ border: '2px solid #0F52BA' }}>
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      className="w-full"
                      videoConstraints={{ facingMode: 'user' }}
                    />
                  </div>
                  <div className="mt-3 space-y-2">
                    {faceCount < 0 ? (
                      <div className="text-center p-2 rounded" style={{ background: '#f5f5f5' }}>
                        <p className="text-xs" style={{ color: '#999' }}>Initializing face detection...</p>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold" style={{ color: '#666' }}>Face Count:</span>
                          <Badge className={faceCount <= 1 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                            {faceCount} {faceCount === 1 ? 'face' : 'faces'}
                          </Badge>
                        </div>
                        <div
                          className="flex items-center justify-center gap-2 p-2 rounded"
                          style={{ background: faceCount <= 1 ? '#f0fdf4' : '#fef3c7' }}
                        >
                          <div className={`w-2 h-2 rounded-full ${faceCount <= 1 ? 'bg-green-500' : 'bg-orange-500'}`} />
                          <span
                            className="text-xs font-medium"
                            style={{ color: faceCount <= 1 ? '#166534' : '#92400e' }}
                          >
                            {faceCount <= 1 ? '✓ Normal' : '⚠️ Multiple people detected'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Session Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Session Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: '#666' }}>Time elapsed:</span>
                  <span className="font-semibold">
                    {Math.floor(sessionTime / 60)}m {sessionTime % 60}s
                  </span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#666' }}>Mouse activity:</span>
                  <span className="font-semibold">{mouseActivity} moves</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#666' }}>Tab switches:</span>
                  <span
                    className="font-semibold"
                    style={{ color: tabSwitchCount > 2 ? '#ef4444' : '#10b981' }}
                  >
                    {tabSwitchCount} times
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Violations Log */}
            {violations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Security Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {violations.map((v, index) => (
                      <div
                        key={index}
                        className="p-2 rounded text-xs"
                        style={{ background: '#fee', borderLeft: '3px solid #ef4444' }}
                      >
                        <p className="font-semibold" style={{ color: '#ef4444' }}>
                          {v.type}
                        </p>
                        <p style={{ color: '#666' }}>{v.message}</p>
                        <p style={{ color: '#999' }}>{v.timestamp.toLocaleTimeString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Security Notice */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4" style={{ color: '#0F52BA' }} />
                  Phase 1: Face Monitoring
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="text-xs space-y-2" style={{ color: '#666' }}>
                  <li>✓ Real-time face detection (MediaPipe)</li>
                  <li>✓ Detects multiple people</li>
                  <li>✓ Warning at 2-3 people</li>
                  <li>✓ Lock at 4+ people</li>
                  <li>✓ Backend policy enforcement</li>
                  <li>✓ No image storage (Phase 1)</li>
                  <li className="text-gray-400 line-through">Audio monitoring (Phase 2)</li>
                  <li className="text-gray-400 line-through">Screen detection (Phase 2)</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
      )}
    </>
  );
}
